// Dynamic numeric Y-axis width.
//
// Recharts' <YAxis> uses a fixed `width`, so a small value clips long tick
// labels (e.g. "22.24" shows as ".24") while a large value wastes space on
// short ones. This sizes the axis to the widest *formatted* tick label using
// real text metrics (canvas measureText), clamped to a floor/ceiling — long
// labels get room, short ones stay tight.

// A shared, lazily-created 2D context for text measurement.
let _ctx: CanvasRenderingContext2D | null | undefined
function measureCtx(): CanvasRenderingContext2D | null {
  if (_ctx !== undefined) return _ctx
  // getContext can be unavailable (SSR) or unimplemented (jsdom) — fall back to
  // a char-width estimate rather than throwing.
  try {
    _ctx =
      typeof document !== 'undefined'
        ? document.createElement('canvas').getContext('2d')
        : null
  } catch {
    _ctx = null
  }
  return _ctx
}

let _fontFamily: string | undefined
function bodyFontFamily(): string {
  if (_fontFamily !== undefined) return _fontFamily
  _fontFamily =
    (typeof window !== 'undefined' &&
      getComputedStyle(document.body).fontFamily) ||
    'ui-sans-serif, system-ui, -apple-system, sans-serif'
  return _fontFamily
}

const _cache = new Map<string, number>()

/** Pixel width of `text` at the given font size (in the app's body font). */
export function measureTextWidth(text: string, fontSizePx: number): number {
  const font = `${fontSizePx}px ${bodyFontFamily()}`
  const key = `${font} ${text}`
  const cached = _cache.get(key)
  if (cached !== undefined) return cached
  const ctx = measureCtx()
  // jsdom / no-canvas fallback: ~0.6em per glyph is close enough for digits.
  const w = ctx ? ((ctx.font = font), ctx.measureText(text).width) : text.length * fontSizePx * 0.6
  _cache.set(key, w)
  return w
}

/** Compact numeric tick label: integers as-is, floats to <=4 significant
 *  digits, so "5.895732" -> "5.896" and "22.24" stays "22.24". Bounding the
 *  decimals also bounds how wide the axis can get. */
export function numericTickFormatter(v: number): string {
  if (!Number.isFinite(v)) return ''
  if (Number.isInteger(v)) return String(v)
  return String(Number(v.toPrecision(4)))
}

export interface NumericYAxisOptions {
  /** Must match the <YAxis> tick font-size so measurement is accurate. */
  fontSizePx?: number
  /** Tick line + gap slack added to the measured text. */
  padding?: number
  /** Width floor / ceiling (px). */
  min?: number
  max?: number
  /** Tick formatter; defaults to numericTickFormatter. Pass the SAME one you
   *  give <YAxis tickFormatter> so measured labels match rendered ones. */
  format?: (v: number) => string
}

/** A "nice" step (1/2/5 × 10ⁿ) at least as large as `raw`. */
function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const norm = raw / mag
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10
  return nice * mag
}

/**
 * Approximate the tick LABELS a numeric Recharts axis will render over
 * [min, max]. Recharts (with a dataMin/dataMax domain) anchors the first tick
 * at the min and steps up by a nice interval, so the interior ticks inherit the
 * min's fraction — e.g. [2.24, 40] → "2.24", "12.24", "22.24", "32.24", "40".
 * Those interior labels are WIDER than either endpoint, so measuring only the
 * endpoints under-sizes the axis. We reproduce that tick series (anchored at
 * min, nice step) and format each with the same formatter the axis uses.
 */
function sampleTickLabels(
  min: number,
  max: number,
  targetCount: number,
  format: (v: number) => string,
): string[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return []
  if (min === max) return [format(min)]
  const step = niceStep((max - min) / Math.max(1, targetCount - 1))
  const labels = new Set<string>([format(min), format(max)])
  // Anchor at min (matching Recharts) and walk up; guard the loop count.
  for (let v = min, i = 0; v <= max && i < 64; v += step, i++) {
    labels.add(format(Number(v.toFixed(12))))
  }
  return [...labels]
}

/**
 * Width (px) for a numeric <YAxis> sized to its widest *tick* label (not just
 * the domain endpoints — interior ticks can be wider; see sampleTickLabels).
 * Returns a value clamped to [min, max]. Pass the SAME `format` you give
 * <YAxis tickFormatter> so measured labels match rendered ones.
 */
export function numericYAxisWidth(
  domainMin: number,
  domainMax: number,
  opts: NumericYAxisOptions = {},
): number {
  const {
    fontSizePx = 12,
    padding = 10,
    min = 28,
    max = 80,
    format = numericTickFormatter,
  } = opts
  const widest = sampleTickLabels(domainMin, domainMax, 5, format).reduce(
    (m, label) => Math.max(m, measureTextWidth(label, fontSizePx)),
    0,
  )
  return Math.round(Math.min(max, Math.max(min, widest + padding)))
}

/** Finite-numeric extent [min, max] of `values` ([0,0] when none). */
export function numericExtent(values: Array<number | null | undefined>): [number, number] {
  let lo = Infinity
  let hi = -Infinity
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      if (v < lo) lo = v
      if (v > hi) hi = v
    }
  }
  return Number.isFinite(lo) ? [lo, hi] : [0, 0]
}
